/**
 * World Management Commands for REAVERS Terminal Interface
 *
 * Integrates with existing terminal stream to provide world management
 * functionality while maintaining Apple II+ inspired aesthetic.
 *
 * Usage: Include this file and call WorldCommands.init() after DOM ready
 */

import { apiBase, offerJoin, rehostMedia } from './platform/platform.js';

// The universe the lobby browses by default. Mirrors the gateway's
// DEFAULT_DATABASE (routes/gateway_world.py). See selectedDatabase below for
// why this must NOT be null.
const DEFAULT_DATABASE = 'plug_engine_reavers';

const WorldCommands = {
    // State management
    state: {
        currentPage: 0,
        lastWorldList: [],
        creationFlow: { active: false, step: 'idle', data: {} },
        authToken: null,
        availableUniverses: [],  // Phase 3: Cached universe list from API
        // Database for WMS API calls.
        //
        // ⚠️ DO NOT default this to null ([2026-07-12-020-ENGINE]). null means
        // "query ALL universes", and WMS answers that by SERIALLY awaiting a
        // per-universe fan-out (service_core.py list_worlds) — rebuilding the
        // whole set on EVERY page request. Measured ~21s per page, which lands
        // on the FIRST screen after login: the lobby sits on "Loading worlds…"
        // for tens of seconds, and callWorldAPI has no timeout and retries 5xx
        // 3x with backoff, so a slow WMS turns that into minutes and then a
        // blank lobby with no error.
        //
        // The lobby is a single-universe surface. Pin it to the universe we
        // ship; a universe picker may still set this explicitly.
        selectedDatabase: DEFAULT_DATABASE
    },

    // Initialize world commands integration
    init() {
        this.initializeAuthentication();
        console.log('World Commands initialized');
    },

    // Initialize Authentication (now uses UnifiedAuthService)
    initializeAuthentication() {
        if (typeof UnifiedAuthService !== 'undefined') {
            // Use unified authentication service
            UnifiedAuthService.onAuthStateChanged((user, token) => {
                this.state.authToken = token;
                console.log('World Commands: Auth state updated', !!user);
            });
        } else if (typeof firebase !== 'undefined' && firebase.auth) {
            // Fallback to direct Firebase auth
            console.warn('UnifiedAuthService not available - using direct Firebase auth');
            firebase.auth().onAuthStateChanged((user) => {
                if (user) {
                    user.getIdToken().then((token) => {
                        this.state.authToken = token;
                    });
                } else {
                    this.state.authToken = null;
                }
            });
        } else {
            console.warn('No authentication service available');
        }
    },

    // Phase 3: Fetch available universes from API
    async fetchAvailableUniverses() {
        try {
            const response = await this.callWorldAPI('/api/worlds/universes', 'GET');
            if (response.success && response.universes) {
                this.state.availableUniverses = response.universes;
                console.log('World Commands: Fetched universes', response.universes.length);
                return response.universes;
            }
        } catch (error) {
            console.error('Failed to fetch universes:', error);
        }
        return [];
    },

    // Main command handler - returns true if command was processed
    async handleCommand(input) {
        console.log('WorldCommands.handleCommand called with:', input);
        console.log('Flow state:', this.state.creationFlow);
        const trimmed = input.trim().toLowerCase();

        // Check if this is a world command
        if (!trimmed.startsWith('/world') && !trimmed.startsWith('world')) {
            // Only intercept if WMS world-creation wizard is active.
            // GameServer character creation (AWAITING_NARRATION/CHARACTER_CHOICE)
            // routes through GameServerConnection.processInput(), not here.
            if (this.state.creationFlow.active) {
                return await this.handleCreationFlowInput(input.trim());
            }
            return false;
        }

        // Parse command and arguments
        const parts = trimmed.split(/\s+/);
        const command = parts[0].replace('/', '');
        const subcommand = parts[1];
        const args = parts.slice(2);

        console.log('Command:', command, 'Subcommand:', subcommand, 'Args:', args);

        if (command !== 'world') {
            return false;
        }

        try {
            switch (subcommand) {
                case 'list':
                case 'ls':
                    await this.handleWorldList(args);
                    return true;
                case 'create':
                case 'new':
                    console.log('Routing to handleWorldCreate');
                    await this.handleWorldCreate();
                    return true;
                case 'join':
                case 'j':
                    await this.handleWorldJoin(args);
                    return true;
                case 'reconnect':
                case 'last':
                    await this.handleWorldReconnect();
                    return true;
                case 'share':
                    await this.handleWorldShare();
                    return true;
                case 'help':
                case '?':
                    this.showWorldHelp();
                    return true;
                default:
                    this.showWorldHelp();
                    return true;
            }
        } catch (error) {
            console.error('World command error:', error);
            this.addMessage(`Error: ${error.message}`);
            return true;
        }
    },

    // Handle world list command
    async handleWorldList(args) {
        let page = 0;
        if (args.length > 0 && !isNaN(parseInt(args[0]))) {
            page = parseInt(args[0]);
        }

        this.addMessage('Loading available worlds...');

        try {
            const response = await this.callWorldAPI('/api/worlds/list', 'GET', {
                page,
                database: this.state.selectedDatabase
            });

            if (response.success) {
                this.state.lastWorldList = response.raw_worlds || [];
                this.state.currentPage = page;
                this.displayWorldList(response);
            } else {
                this.addMessage(`Failed to load worlds: ${response.error}`);
            }
        } catch (error) {
            this.addMessage(`Connection error: ${error.message}`);
        }
    },

    // Handle world creation command
    async handleWorldCreate() {
        console.log('handleWorldCreate called');
        console.log('Auth token:', this.state.authToken ? 'present' : 'missing');

        if (!this.state.authToken) {
            this.addMessage('Please sign in to create worlds');
            return;
        }

        this.addMessage('Initiating world creation...');

        // Phase 3: Fetch available universes first
        const universes = await this.fetchAvailableUniverses();
        if (universes.length === 0) {
            this.addMessage('No universes available. Please try again later.');
            return;
        }

        try {
            console.log('About to call API...');
            const response = await this.callWorldAPI('/api/worlds/create', 'POST', {
                database: this.state.selectedDatabase
            });
            console.log('World create response:', response);

            if (response && response.success && response.flow) {
                console.log('Processing flow:', response.flow);
                // Use flow data from backend
                this.state.creationFlow = {
                    active: true,
                    step: response.flow.step,
                    data: {}
                };
                console.log('✅ Flow state set to:', this.state.creationFlow);
                console.log('🔍 Verifying state persists:', WorldCommands.state.creationFlow);

                // Display appropriate prompt based on backend's flow step
                this.processCreationFlowResponse(response.flow);
            } else {
                console.log('No flow in response or not successful:', response);
                if (response && response.action === 'show_limits') {
                    this.displayCreationLimits(response);
                } else {
                    this.addMessage(`Cannot create world: ${response ? response.error : 'No response'}`);
                }
            }
        } catch (error) {
            console.error('World create error:', error);
            this.addMessage(`Creation error: ${error.message}`);
        }
    },

    // Handle world join command
    async handleWorldJoin(args) {
        if (!this.state.authToken) {
            this.addMessage('Please sign in to join worlds');
            return;
        }

        if (args.length === 0) {
            this.addMessage('Please specify a world number or ID to join');
            this.addMessage('Usage: /world join [number] or /world join [world_id]');
            return;
        }

        const target = args[0];
        let worldNumber = null;
        let worldId = null;

        if (!isNaN(parseInt(target))) {
            worldNumber = parseInt(target);
            if (this.state.lastWorldList.length === 0) {
                this.addMessage('No worlds loaded. Use /world list first');
                return;
            }
            if (worldNumber < 1 || worldNumber > this.state.lastWorldList.length) {
                this.addMessage(`Invalid world number. Available: 1-${this.state.lastWorldList.length}`);
                return;
            }
            worldId = this.state.lastWorldList[worldNumber - 1].world_id;
        } else {
            worldId = target;
        }

        let password = null;
        if (args.length > 1) {
            password = args.slice(1).join(' ');
        }

        try {
            const response = await this.callWorldAPI('/api/worlds/join', 'POST', {
                world_id: worldId,
                world_number: worldNumber,
                password: password,
                database: this.state.selectedDatabase
            });

            if (response.success) {
                this.addMessage(`Joining world: ${response.connection_details.world_name}`);
                await this.connectToGameServer(response.connection_details);
            } else {
                if (response.error.includes('password')) {
                    this.addMessage(response.error);
                    this.addMessage('Try: /world join [number] [password]');
                } else {
                    this.addMessage(response.error);
                }
            }
        } catch (error) {
            this.addMessage(`Join error: ${error.message}`);
        }
    },

    // Handle world reconnect
    // Share the world you are in: the server mints a join token from YOUR live
    // seat (never from anything this client says), and the platform offers it.
    async handleWorldShare() {
        const resp = await this.callWorldAPI('/api/worlds/share', 'POST', { cause: 'share-link' });
        if (!resp || !resp.success || !resp.join_token) {
            this.addMessage(`Share failed: ${(resp && (resp.error || resp.detail)) || 'no token returned'}`);
            return;
        }
        console.log(`[share] join_token=${resp.join_token} world=${resp.world_id}`);
        const offer = await offerJoin(resp.join_token, 'Join my party in Reavers');
        if (!offer.shared) {
            this.addMessage('Nothing was shared: the share window closed before the link was sent or copied.');
            return;
        }
        if (offer.link) this.addMessage(`Your link: ${offer.link}`);
        const how = offer.sent ? 'Link sent.' : offer.copied ? 'Link copied.' : 'Shared.';
        this.addMessage(`${how} A friend who follows it lands in your world, on your road.`);
    },

    async handleWorldReconnect() {
        if (!this.state.authToken) {
            this.addMessage('Please sign in to reconnect to worlds');
            return;
        }

        this.addMessage('Checking for previous world...');

        try {
            const response = await this.callWorldAPI('/api/worlds/reconnect', 'POST', {
                database: this.state.selectedDatabase
            });

            if (response.success) {
                this.addMessage(response.message);
                await this.connectToGameServer(response.connection_details);
            } else {
                this.addMessage(response.error);
                if (response.suggestion) {
                    this.addMessage(response.suggestion);
                }
            }
        } catch (error) {
            this.addMessage(`Reconnect error: ${error.message}`);
        }
    },

    // Handle creation flow input
    async handleCreationFlowInput(input) {
        if (!this.state.creationFlow.active) {
            return false;
        }

        // If on universe_selection step, resolve user input to universe name
        let processedInput = input;
        if (this.state.creationFlow.step === 'universe_selection') {
            const selection = parseInt(input);
            if (selection >= 1 && selection <= this.state.availableUniverses.length) {
                const selectedUniverse = this.state.availableUniverses[selection - 1];
                // Send universe NAME to backend (not UUID)
                processedInput = selectedUniverse.name;
                // Store locally for display purposes
                this.state.creationFlow.data.universe_name = selectedUniverse.name;
                // Set database for subsequent API calls
                this.state.selectedDatabase = `plug_engine_${selectedUniverse.name}`;
                this.state.creationFlow.data.database = this.state.selectedDatabase;
                console.log('Universe selection: sending name:', selectedUniverse.name);
                console.log('Database set to:', this.state.selectedDatabase);
            }
        }

        try {
            const response = await this.callWorldAPI('/api/worlds/create/input', 'POST', {
                input: processedInput,
                database: this.state.selectedDatabase
            });

            if (response.completed) {
                this.state.creationFlow.active = false;
                this.addMessage(response.message);
                if (response.world_info) {
                    await this.connectToGameServer(response.world_info);
                }
            } else if (response.cancelled) {
                this.state.creationFlow.active = false;
                this.addMessage(response.message);
            } else if (response.success === false) {
                this.addMessage(response.error);
                if (response.message) {
                    this.addMessage(response.message);
                }
                // Keep flow active on validation errors so user can retry
            } else if (response.flow) {
                // Update frontend state to match backend flow
                this.state.creationFlow = {
                    active: true,
                    step: response.flow.step,
                    data: this.state.creationFlow.data || {}
                };
                this.processCreationFlowResponse(response.flow);
            }

            return true;
        } catch (error) {
            this.addMessage(`Creation flow error: ${error.message}`);
            this.state.creationFlow.active = false;
            return true;
        }
    },

    // Process creation flow response
    processCreationFlowResponse(flowData) {
        if (flowData.message) {
            this.addMessage(flowData.message);
        }
        this.addMessage('');

        switch (flowData.step) {
            case 'universe_selection':
                this.displayUniverseSelection();
                break;
            case 'world_name':
                this.addMessage(flowData.prompt);
                break;
            case 'password_setup':
                this.addMessage(flowData.prompt);
                this.addMessage('(Type ]none for no password)');
                break;
            case 'max_players':
                this.addMessage(flowData.prompt);
                this.addMessage('(Press Enter for default: 32 players)');
                break;
            case 'confirmation':
                this.displayWorldCreationSummary(flowData);
                break;
            default:
                if (flowData.prompt) {
                    this.addMessage(flowData.prompt);
                }
                if (flowData.options && flowData.options.length > 0) {
                    flowData.options.forEach(option => {
                        this.addMessage(option);
                    });
                }
        }

        if (flowData.progress) {
            this.addMessage(`Progress: ${flowData.progress}%`);
        }
    },

    // Display universe selection (Phase 3: Dynamic from API)
    displayUniverseSelection() {
        this.addMessage('Choose your universe:');
        this.addMessage('');

        const universes = this.state.availableUniverses;
        if (universes.length === 0) {
            // Fallback if no universes loaded
            this.addMessage('No universes available. Please try again later.');
            return;
        }

        universes.forEach((universe, index) => {
            const num = index + 1;
            const desc = universe.description || 'No description available';
            this.addMessage(`${num}. ${universe.name} - ${desc}`);
        });

        this.addMessage('');
        this.addMessage(`Type 1-${universes.length} to select, or "cancel" to abort.`);
    },

    // Display world creation summary
    displayWorldCreationSummary(flowData) {
        this.addMessage('World Creation Summary');
        this.addMessage('');

        if (flowData.world_summary) {
            const summary = flowData.world_summary;
            this.addMessage(`Name: ${summary.name}`);
            // Phase 3: Backend now sends 'universe' (name) instead of 'universe_template'
            this.addMessage(`Universe: ${summary.universe || summary.universe_template || 'Unknown'}`);
            this.addMessage(`Password: ${summary.has_password ? 'Yes' : 'No'}`);
            this.addMessage(`Max Players: ${summary.max_players}`);
        }

        this.addMessage('');
        this.addMessage('Type "confirm" to create this world, or "cancel" to abort.');
    },

    // Display creation limits
    displayCreationLimits(response) {
        this.addMessage('World Creation Limits Reached');
        this.addMessage('');

        if (response.limits) {
            const limits = response.limits;
            this.addMessage(`Active worlds: ${limits.active_worlds}/${limits.max_worlds_per_player}`);
            this.addMessage(`Today's creations: ${limits.worlds_created_today}/${limits.max_worlds_per_day}`);
            if (limits.cooldown_remaining_minutes > 0) {
                this.addMessage(`Cooldown: ${limits.cooldown_remaining_minutes.toFixed(1)} minutes`);
            }
        }

        if (response.reasons && response.reasons.length > 0) {
            this.addMessage('');
            this.addMessage('Reasons:');
            response.reasons.forEach(reason => {
                this.addMessage(`- ${reason}`);
            });
        }
    },

    // Display world list
    displayWorldList(response) {
        this.addMessage('Available Worlds');
        this.addMessage('');

        if (response.worlds.length === 0) {
            this.addMessage('No worlds currently available.');
            this.addMessage('Be the first to create one with /world create');
            return;
        }

        response.worlds.forEach((worldText) => {
            // Clean up the formatted text to remove emojis and styling
            const cleanText = worldText
                .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters (emojis)
                .replace(/\*\*/g, '') // Remove markdown bold
                .replace(/\[Password Required\]/g, '[PASSWORD]')
                .replace(/\[FULL\]/g, '[FULL]')
                .replace(/\[Starting\.\.\.\]/g, '[STARTING]');
            this.addMessage(cleanText);
        });

        this.addMessage('');

        if (response.has_more) {
            this.addMessage(`Type '/world list ${response.page + 1}' to see more worlds.`);
        }

        this.addMessage(response.instructions);
    },

    // Show world help
    showWorldHelp() {
        this.addMessage('World Management Commands');
        this.addMessage('');
        this.addMessage('/world list [page] - List available worlds');
        this.addMessage('/world create - Create a new world');
        this.addMessage('/world join [number] - Join a world by number');
        this.addMessage('/world join [number] [password] - Join with password');
        this.addMessage('/world reconnect - Reconnect to your last world');
        this.addMessage('/world help - Show this help');
        this.addMessage('');
        this.addMessage('Examples:');
        this.addMessage('  /world list');
        this.addMessage('  /world join 1');
        this.addMessage('  /world join 2 mypassword');
    },

    // Connect to GameServer. GameServerConnection is the ONE socket path;
    // main.js imports it before this module runs, so its absence is a
    // packaging defect and refuses loudly rather than opening a second socket.
    async connectToGameServer(connectionDetails) {
        // Server-authoritative identity: player_id is derived from JWT on the
        // server side. We no longer set it from the client.
        if (typeof GameServerConnection === 'undefined') {
            console.error('[world-commands] GameServerConnection is not loaded; refusing to connect.');
            this.addMessage('Connection is unavailable (client failed to load). Please reload.');
            return;
        }
        // FSM drives state transitions. The FSM transitions:
        //   CONNECTING -> CREATION_FLOW or WORLD_MENU
        //   _applyMode creates new input line when transitioning to an open state.
        await GameServerConnection.connect(connectionDetails);
    },

    // Call World API with unified authentication
    /** ` -- <detail>` from a FastAPI error body, or '' when it has none. */
    async _errorDetail(response) {
        try {
            const body = await response.json();
            return body && body.detail ? ` -- ${typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)}` : '';
        } catch (_e) {
            return '';
        }
    },

    async callWorldAPI(endpoint, method = 'GET', data = {}, retryOptions = {}) {
        const defaultRetryOptions = {
            maxRetries: 3,
            retryDelay: 1000, // Start with 1 second
            backoffMultiplier: 2, // Exponential backoff
            retryableStatus: [500, 502, 503, 504, 429], // Server errors and rate limits
            showRetryMessages: true
        };

        const options = { ...defaultRetryOptions, ...retryOptions };
        let lastError = null;

        for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
            try {
                // Control-plane API origin, from the platform seam: gameAPI's
                // absolute origin on the web build (it lives in a different
                // project than the hosting site, so no relative rewrite reaches
                // it), the URL-mapped /api prefix in the Activity, '' in local
                // dev so the Vite proxy answers.
                const baseUrl = apiBase();

                let url = endpoint;
                if (!url.startsWith('/api/worlds')) {
                    url = `/api/worlds${endpoint.replace('/api/worlds', '')}`;
                }
                url = baseUrl + url;

                const fetchOptions = {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json'
                    }
                };

                // Use UnifiedAuthService for authentication headers
                try {
                    if (typeof UnifiedAuthService !== 'undefined' && UnifiedAuthService.isAuthenticated()) {
                        const authHeaders = await UnifiedAuthService.getAuthHeaders();
                        Object.assign(fetchOptions.headers, authHeaders);
                    } else if (this.state.authToken) {
                        // Fallback to direct token usage
                        fetchOptions.headers['Authorization'] = `Bearer ${this.state.authToken}`;
                    }
                } catch (authError) {
                    console.warn('Authentication header error:', authError.message);
                    // Continue without auth headers for unauthenticated endpoints
                }

                if (method === 'POST' && Object.keys(data).length > 0) {
                    fetchOptions.body = JSON.stringify(data);
                } else if (method === 'GET' && Object.keys(data).length > 0) {
                    const params = new URLSearchParams(data);
                    url += `?${params}`;
                }

                const response = await fetch(url, fetchOptions);

                if (!response.ok) {
                    // Handle specific error types
                    if (response.status === 401) {
                        await this.handleAuthenticationError(response, attempt);
                        // Don't retry 401 errors - they need authentication fix
                        throw new Error(`Authentication failed: ${response.status}`);
                    } else if (response.status === 403) {
                        // Don't retry forbidden errors. The server's detail says
                        // WHY (a refused launch names its reason); keep it.
                        throw new Error(`Access forbidden: ${response.status}${await this._errorDetail(response)}`);
                    } else if (response.status === 404) {
                        // Don't retry not found errors
                        throw new Error(`Endpoint not found: ${response.status}`);
                    } else if (options.retryableStatus.includes(response.status)) {
                        // These are retryable errors
                        const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                        if (attempt < options.maxRetries) {
                            if (options.showRetryMessages) {
                                this.addMessage(`⚠️ ${errorMessage} - Retrying in ${options.retryDelay * Math.pow(options.backoffMultiplier, attempt)}ms... (${attempt + 1}/${options.maxRetries + 1})`);
                            }
                            await this.delay(options.retryDelay * Math.pow(options.backoffMultiplier, attempt));
                            lastError = new Error(errorMessage);
                            continue; // Retry
                        } else {
                            throw new Error(errorMessage);
                        }
                    } else {
                        // Non-retryable client error
                        throw new Error(`HTTP ${response.status}: ${response.statusText}${await this._errorDetail(response)}`);
                    }
                }

                // Success - parse and return response. Media URLs in it are
                // rehosted for the Activity (the platform seam; identity on web).
                const responseData = rehostMedia(await response.json());

                // Show success message for retried requests
                if (attempt > 0 && options.showRetryMessages) {
                    this.addMessage(`✅ Request succeeded after ${attempt + 1} attempts`);
                }

                return responseData;

            } catch (fetchError) {
                lastError = fetchError;

                // Check if this is a network error that should be retried
                const isNetworkError = fetchError.message.includes('fetch') ||
                                     fetchError.message.includes('network') ||
                                     fetchError.message.includes('timeout') ||
                                     fetchError.name === 'TypeError';

                if (isNetworkError && attempt < options.maxRetries) {
                    if (options.showRetryMessages) {
                        this.addMessage(`🔄 Network error - Retrying in ${options.retryDelay * Math.pow(options.backoffMultiplier, attempt)}ms... (${attempt + 1}/${options.maxRetries + 1})`);
                    }
                    await this.delay(options.retryDelay * Math.pow(options.backoffMultiplier, attempt));
                    continue; // Retry network errors
                }

                // Non-retryable error or max retries reached
                if (attempt === options.maxRetries) {
                    console.error('API call failed after all retries:', {
                        url: endpoint,
                        method,
                        attempts: attempt + 1,
                        error: fetchError.message
                    });

                    if (options.showRetryMessages) {
                        this.addMessage(`❌ Request failed after ${attempt + 1} attempts: ${fetchError.message}`);
                    }
                }

                throw fetchError;
            }
        }

        // This should never be reached, but include for completeness
        throw lastError || new Error('Unknown error in API retry logic');
    },

    // Utility method for delays
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    // Handle authentication errors
    async handleAuthenticationError(response, attempt = 0) {
        console.error('Authentication error in API call:', response.status, response.statusText);

        if (typeof UnifiedAuthService !== 'undefined') {
            try {
                // Try to refresh token only on first attempt
                if (attempt === 0) {
                    this.addMessage('🔄 Refreshing authentication token...');
                    await UnifiedAuthService.refreshToken(true);
                    this.addMessage('✅ Authentication token refreshed');
                } else {
                    // Multiple auth failures indicate a deeper issue
                    this.addMessage('❌ Authentication failed. Please sign in again.');
                }
            } catch (error) {
                console.error('Token refresh failed:', error.message);
                this.addMessage('❌ Authentication expired. Please sign in again.');

                // Optional: Trigger sign-in flow
                if (UnifiedAuthService.triggerSignIn) {
                    setTimeout(() => {
                        UnifiedAuthService.triggerSignIn();
                    }, 2000);
                }
            }
        } else {
            this.addMessage('❌ Authentication service not available. Please refresh the page.');
        }
    },

    // Handle server errors
    handleServerError(response) {
        console.error('Server error in API call:', response.status, response.statusText);
        this.addMessage('Server error. Please try again in a moment.');
    },

    /**
     * THE SESSION-NOTICE SINK. [2026-08-08-007-ENGINE]
     *
     * ⚠️ READ THIS BEFORE ADDING A CALLER. This one function is where the
     * "~40 semantically distinct origins wearing one class" problem lives:
     * `GameServerConnection.logMessage` funnels into it, and between them they
     * carry world-menu output, auth notices, connection state, errors, `/debug`
     * dumps and creation-flow prose — all as byte-identical
     * `.message ai-response`, so nothing downstream could tell them apart.
     *
     * ITS DECLARED AXIS IS `game-state-transition` — auth, world select,
     * character creation. That is what the overwhelming majority of its ~200
     * callers actually are, and it is the STRICTEST of the three non-content
     * axes: it forbids a channel AND a cycle_id. Defaulting to the strictest
     * row means anything needing a beat coordinate has to say so out loud
     * rather than acquiring one by accident.
     *
     * A caller on a different axis passes it: `{ origin: ORIGIN.SERVER_AUTHORITY }`
     * for a control-plane fact that carries a cycle.
     *
     * ⛔ GAME CONTENT DOES NOT COME THROUGH HERE AT ALL. ch4 dialogue used to,
     * which is why it was invisible to the Terminal Truth Log and to
     * `exportGameplayLog`; it now goes to the funnel directly as
     * `ledger-content` on channel 4. If you are about to route a channel
     * payload through this sink, you are re-opening leak 5.
     *
     * @param {string} message
     * @param {{origin?: string, cycleId?: string, className?: string}} [opts]
     */
    addMessage(message, opts) {
        const o = opts || {};
        const F = (typeof window !== 'undefined') ? window.TerminalFunnel : null;
        if (!F) {
            // No funnel => no door => nothing renders. This is the headless /
            // unit-harness path and the pre-DOM path, and it must stay LOUD
            // rather than quietly reaching for `appendChild`: every caller of
            // the OLD helper had exactly such a fallback, which is why the old
            // helper was never load-bearing and the boundary never existed.
            console.log('[WorldCommands]', message);
            return null;
        }
        return F.write(message, {
            origin: o.origin || F.ORIGIN.GAME_STATE_TRANSITION,
            cycleId: o.cycleId,
            className: o.className || 'message ai-response'
        });
    },

    // Escape HTML to prevent XSS
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
};

// Make WorldCommands available globally
window.WorldCommands = WorldCommands;

// ES Module export
export default WorldCommands;